import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { apiPost } from '../../utils/api';

export default function ConversationListItem({ conversation, isActive, onClick, onConversationsUpdated }) {
    const { theme } = useTheme();
    const { currentUser } = useAuth();
    const isPending = conversation.membership_status === 'pending';

    const handleAccept = async (e) => {
        e.stopPropagation();
        try {
            const response = await apiPost(`/conversations/${conversation.id}/accept`);
            const data = await response.json();
            if (response.ok && data.conversation) {
                onConversationsUpdated?.(prev =>
                    prev.map(c => c.id === conversation.id ? data.conversation : c)
                );
            }
        } catch (err) {
            console.error('Failed to accept invite', err);
        }
    };

    const handleDecline = async (e) => {
        e.stopPropagation();
        try {
            const response = await apiPost(`/conversations/${conversation.id}/decline`);
            if (response.ok) {
                onConversationsUpdated?.(prev => prev.filter(c => c.id !== conversation.id));
            }
        } catch (err) {
            console.error('Failed to decline invite', err);
        }
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diff = now - date;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days === 0) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
        if (days === 1) return 'Yesterday';
        if (days < 7) return date.toLocaleDateString([], { weekday: 'short' });
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    };

    const getInitial = () => {
        if (conversation.conversation_type === 'group') {
            return (conversation.display_name || conversation.title || 'G').charAt(0).toUpperCase();
        }
        const otherMember = conversation.members?.find(m => m.id !== currentUser?.id);
        if (otherMember) {
            return (otherMember.first_name || otherMember.username || '').charAt(0).toUpperCase() || '?';
        }
        return (conversation.display_name || '').charAt(0).toUpperCase() || '?';
    };

    const getAvatar = () => {
        if (conversation.conversation_type === 'group') {
            return (
                <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white text-sm font-unna font-medium flex-shrink-0">
                    {getInitial()}
                </div>
            );
        }

        const otherMember = conversation.members?.find(m => m.id !== currentUser?.id);
        if (otherMember?.avatar_url) {
            return <img src={otherMember.avatar_url} alt="" className="w-10 h-10 rounded-full flex-shrink-0 object-cover" />;
        }

        return (
            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-unna font-medium flex-shrink-0">
                {getInitial()}
            </div>
        );
    };

    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg transition-colors text-left ${
                isActive
                    ? theme === 'dark' ? 'bg-blue-900/30 border-l-2 border-blue-500' : 'bg-blue-50 border-l-2 border-blue-500'
                    : theme === 'dark' ? 'hover:bg-gray-700/50 border-l-2 border-transparent' : 'hover:bg-gray-50 border-l-2 border-transparent'
            }`}
        >
            {getAvatar()}

            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                    <p className={`text-sm font-unna truncate ${
                        conversation.unread_count > 0 ? 'font-semibold' : 'font-medium'
                    }`}>
                        {conversation.display_name}
                    </p>
                    <span className={`text-xs font-rasa flex-shrink-0 ml-2 ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                        {formatTime(conversation.last_message?.created_at || conversation.last_message_at)}
                    </span>
                </div>
                {isPending ? (
                    <div className="flex flex-col gap-1 mt-1">
                        <div className="flex items-center gap-1.5">
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                                Invitation
                            </span>
                            {conversation.inviter && (
                                <span className={`text-xs font-rasa truncate ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                    Invited by {conversation.inviter.first_name} {conversation.inviter.last_name}
                                </span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={handleAccept}
                                className="px-2 py-0.5 text-xs rounded bg-blue-500 text-white hover:bg-blue-600"
                            >
                                Accept
                            </button>
                            <button
                                onClick={handleDecline}
                                className={`px-2 py-0.5 text-xs rounded border ${
                                    theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                Decline
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-between mt-0.5">
                        <p className={`text-xs font-rasa truncate ${
                            conversation.unread_count > 0
                                ? theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                : theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                            {conversation.last_message
                                ? `${conversation.last_message.user?.username}: ${conversation.last_message.content}`
                                : 'No messages yet'
                            }
                        </p>
                        {conversation.unread_count > 0 && (
                            <span className="ml-2 flex-shrink-0 min-w-[20px] h-5 rounded-full bg-blue-600 text-white text-[10px] font-medium flex items-center justify-center px-1.5">
                                {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </button>
    );
}
